import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Sectors", to: "/#sectors" },
  { label: "Governance", to: "/#governance" },
  { label: "Command Center", to: "/#command-center" },
  { label: "Insights", to: "/#insights" },
  { label: "Engage", to: "/#engage" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleAnchor = (e, to) => {
    if (to.startsWith("/#")) {
      const id = to.split("#")[1];
      if (location.pathname === "/") {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        e.preventDefault();
        navigate("/", { state: { scrollTo: id } });
      }
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-canvas/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="container-bharat flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-sm border border-navy/20 bg-navy text-gold font-display text-base">
            <span className="absolute inset-1 rounded-full ashoka-spokes opacity-80" />
            <span className="relative">B</span>
          </span>
          <span className="leading-tight">
            <span className="block font-serif-display text-xl text-navy tracking-tight">
              Bharat Axis
            </span>
            <span className="block overline text-[0.6rem] -mt-0.5">
              Advisory
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={(e) => handleAnchor(e, l.to)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-navy/80 hover:text-navy underline-grow transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href="/#engage"
            data-testid="navbar-engage-cta"
            onClick={(e) => handleAnchor(e, "/#engage")}
            className="inline-flex items-center gap-2 px-5 py-3 text-sm border border-navy bg-navy text-ivory hover:bg-ivory hover:text-navy transition-all duration-300"
          >
            Consulting Inquiry
            <span className="w-4 h-px bg-current transition-all group-hover:w-6" />
          </a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-navy"
          aria-label="Toggle navigation"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            data-testid="navbar-mobile-menu"
            className="lg:hidden border-t border-black/5 bg-canvas/95 backdrop-blur-xl"
          >
            <div className="container-bharat py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.to}
                  href={l.to}
                  onClick={(e) => handleAnchor(e, l.to)}
                  className="text-base text-navy/80 hover:text-navy"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/#engage"
                onClick={(e) => handleAnchor(e, "/#engage")}
                className="mt-2 inline-flex items-center justify-center px-5 py-3 text-sm border border-navy bg-navy text-ivory"
              >
                Consulting Inquiry
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
